import Button, { ButtonTypes } from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { Icons } from "atoms/constants/icons";
import TextInput from "atoms/forms/text-input";
import Icon from "atoms/icons/icon";
import Heading from "atoms/typography/heading";
import Form from "molecules/forms/form";
import React, { useMemo, useRef, useState } from "react";
import useOutsideClick from "utilities/hooks/use-outside-click";
import { t } from "utilities/localization-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";
import CultureResources from "utilities/interfaces/culture-resources";
import StringUtils from "utilities/string-utils";

const CSS_CLASS_NAME = "c-title-field";
const INPUT_FIELD_ID = "title-text-input";
const MAX_LENGTH = 60;
const TOAST_RENAME_ERROR = "TOAST_RENAME_ERROR";

type ValidResourceName = keyof Pick<CultureResources, "notebook" | "page">;

export interface TitleFieldProps {
    editable?: boolean;
    name?: string;
    onSave: (newName: string) => Promise<void>;
    resourceName: ValidResourceName;
}

const TitleField = (props: TitleFieldProps) => {
    const { editable = true, name = "", onSave, resourceName } = props;
    const { isReadonly, skipIfIsReadonly } = useReadonlyContext();
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState("");
    const nameAreaRef = useRef<HTMLDivElement>(null);
    const editedFieldName = `${StringUtils.capitalize(t(resourceName))} ${t("name")}`;

    const handleOnEditStart = () => {
        setValue(name);
        setIsEditing(true);
    };
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        ToastManager.dismissAll();
        setValue(e.target.value);
    };

    const handleOnEditCancel = () => {
        setValue("");
        setIsEditing(false);
    };

    const handleOnSubmit = skipIfIsReadonly(async (e) => {
        ToastManager.dismiss(TOAST_RENAME_ERROR);
        try {
            await onSave(value);
            ToastManager.success(
                t("success-actionResource", {
                    action: t("updated"),
                    resource: editedFieldName,
                })
            );
            setIsEditing(false);
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("updating"),
                    resource: editedFieldName,
                }),
                { toastId: TOAST_RENAME_ERROR }
            );
        }
    });

    useOutsideClick(nameAreaRef, handleOnEditCancel, "mousedown");

    const titleHeading = useMemo(
        () => (
            <Heading
                cssClassName={`${CSS_CLASS_NAME}__title`}
                priority={HeadingPriority.Two}
                dataTestId={`title-field--${resourceName}--heading-title`}>
                {name}
            </Heading>
        ),
        [name, resourceName]
    );

    if (isReadonly) {
        return <div className={CSS_CLASS_NAME}>{titleHeading}</div>;
    }

    return (
        <div className={CSS_CLASS_NAME} ref={nameAreaRef}>
            {isEditing ? (
                <Form
                    cssClassName={`${CSS_CLASS_NAME}__form`}
                    onSubmit={(e) => e.preventDefault()}>
                    <TextInput
                        dataTestId="title-field--text-input"
                        cssClassName={`${CSS_CLASS_NAME}__input`}
                        id={INPUT_FIELD_ID}
                        onChange={handleOnChange}
                        value={value}
                        maxLength={MAX_LENGTH}
                    />
                    <Button
                        dataTestId="title-field--submit-button"
                        onClick={handleOnSubmit}
                        size={ButtonSizes.XSmall}
                        style={ButtonStyles.Tertiary}
                        type={ButtonTypes.Submit}>
                        <Icon type={Icons.Checkmark} />
                        <span className="sr-only">{t("submit")}</span>
                    </Button>
                    <Button
                        dataTestId="title-field--cancel-button"
                        onClick={handleOnEditCancel}
                        size={ButtonSizes.XSmall}
                        style={ButtonStyles.Destructive}>
                        <Icon type={Icons.Close} />
                        <span className="sr-only">{t("cancel")}</span>
                    </Button>
                </Form>
            ) : (
                <>
                    {titleHeading}
                    <div className={`${CSS_CLASS_NAME}__button`}>
                        {editable && (
                            <Button
                                dataTestId="title-field--edit-button"
                                size={ButtonSizes.XSmall}
                                style={ButtonStyles.Tertiary}
                                onClick={handleOnEditStart}>
                                <Icon type={Icons.Edit} />
                                <span className="sr-only">{t("edit")}</span>
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TitleField;
